'use client';

import { Box, Divider, TextTab } from '@wishket/design-system';
import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

import AuthorArticles from '../AuthorArticles';
import AuthorArticleList from '../AuthorArticleList';

import { AuthorSummary, AuthorIntroduction } from '@/features/Author';
import { PATH } from '@/shared/constants';
import { useAuthorQuery } from '@/entities/author';
import { useProfile, useDeviceCheck } from '@/shared/hooks';
import { Skeleton } from '@/shared/Components';

enum TAB_INDEX {
  HOME = 0,
  CONTENT = 1,
  INTRODUCTION = 2,
}

type TabName = 'home' | 'content' | 'introduction';

const tabMap: Record<TabName, number> = {
  home: 0,
  content: 1,
  introduction: 2,
};

type OrderType = 'latest' | 'popular';

enum ORDER_TYPE {
  LATEST = 'latest',
  POPULAR = 'popular',
}

const AuthorTabSkeleton = () => {
  return (
    <Box className="flex flex-col gap-y-12 px-5 grid-in-content desktop:gap-y-10 desktop:px-0">
      <Skeleton className="h-[60px] desktop:hidden" grayScale={50} />
      {Array.from({ length: 3 }).map((_, index) => (
        <Skeleton key={index} className="h-[294px]" grayScale={50} />
      ))}
    </Box>
  );
};

interface AuthorTabProps {
  urlIdentifier: string;
}

const AuthorTab = ({ urlIdentifier }: AuthorTabProps) => {
  const { data, isLoading } = useAuthorQuery({ urlIdentifier });
  const { profile } = useProfile();
  const { isDesktop } = useDeviceCheck();
  const router = useRouter();
  const searchParams = useSearchParams();
  const paramTab = searchParams.get('tab');
  const [tab, setTab] = useState(TAB_INDEX.HOME);
  const [orderType, setOrderType] = useState<OrderType>(ORDER_TYPE.LATEST);
  const isAvailableTab = paramTab && paramTab in tabMap;
  const currentTab = isAvailableTab ? tabMap[paramTab as TabName] : tab;

  const handleTabChangeIntroduction = () => {
    if (!window) return;
    setTab(TAB_INDEX.INTRODUCTION);
    window.scrollTo({ top: 0 });
  };

  const handleTabChangeNewArticles = () => {
    if (!window) return;
    setTab(TAB_INDEX.CONTENT);
    setOrderType(ORDER_TYPE.LATEST);
    window.scrollTo({ top: 0 });
  };

  const handleTabChangePopularArticles = () => {
    if (!window) return;
    setTab(TAB_INDEX.CONTENT);
    setOrderType(ORDER_TYPE.POPULAR);
    window.scrollTo({ top: 0 });
  };

  const handleTabChange = (index: number) => {
    if (!data) return;

    if (index !== TAB_INDEX.CONTENT) {
      setOrderType(ORDER_TYPE.LATEST);
    }

    setTab(index);

    if (paramTab) {
      router.replace(`${PATH.HOME}/@${data.author.urlIdentifier}`);
    }
  };

  useEffect(() => {
    if (!data) return;

    if (paramTab && !isAvailableTab) {
      router.replace(`${PATH.HOME}/@${data.author.urlIdentifier}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAvailableTab]);

  if (isLoading) return <AuthorTabSkeleton />;

  if (!data) return null;

  return (
    <TextTab.Root
      isFit
      isFull={!isDesktop}
      currentActiveTab={currentTab}
      onChange={handleTabChange}
    >
      <Box className="grid-in-tab desktop:px-0">
        <TextTab.Tabs>
          <TextTab.Tab index={tabMap.home}>홈</TextTab.Tab>
          <TextTab.Tab
            index={tabMap.content}
            badgeNumber={data.author.count.article}
          >
            콘텐츠
          </TextTab.Tab>
          <TextTab.Tab index={tabMap.introduction}>소개</TextTab.Tab>
        </TextTab.Tabs>
      </Box>
      <Box className="pt-2 grid-in-content">
        <TextTab.Panel index={tabMap.home}>
          <Box className="flex flex-col gap-10 desktop:gap-12">
            <AuthorSummary
              isOwner={data.author.urlIdentifier === profile?.authorIdentifier}
              urlIdentifier={urlIdentifier}
              description={data.author.description}
              employments={data.author.employments}
              onClickSeeAll={handleTabChangeIntroduction}
            />
            <Box className="px-5 desktop:px-0">
              <Divider isSub />
            </Box>
            <AuthorArticles
              articleType="new"
              authorId={data.author.id}
              onClickSeeAll={handleTabChangeNewArticles}
            />
            <Box className="px-5 desktop:px-0">
              <Divider isSub />
            </Box>
            <AuthorArticles
              articleType="popular"
              authorName={data.author.name}
              authorId={data.author.id}
              onClickSeeAll={handleTabChangePopularArticles}
            />
          </Box>
        </TextTab.Panel>
        <TextTab.Panel index={tabMap.content}>
          <Box className="px-5 desktop:px-0">
            <AuthorArticleList
              authorId={data.author.id}
              defaultOrder={orderType}
            />
          </Box>
        </TextTab.Panel>
        <TextTab.Panel index={tabMap.introduction}>
          <Box className="px-5 desktop:px-0">
            <AuthorIntroduction
              isOwner={data.author.urlIdentifier === profile?.authorIdentifier}
              urlIdentifier={urlIdentifier}
              description={data.author.description}
              employments={data.author.employments}
              linkUrl={data.author.websiteUrl}
            />
          </Box>
        </TextTab.Panel>
      </Box>
    </TextTab.Root>
  );
};

export default AuthorTab;
